import { useQuery } from "@apollo/client";
import { useActiveBusinessUnit } from "./use-active-business-unit";
import { graphql, type ResultOf } from "@/graphql";

/**
 * Available subscription feature flags
 */
export const SUBSCRIPTION_FEATURES = {
  SUPPLY_CHAINS_APP_ACCESS: "supply-chains-app-access",
  ANIMAL_MANAGEMENT: "animal-management",
} as const;

/**
 * Type for subscription feature flags
 */
export type SubscriptionFeature = (typeof SUBSCRIPTION_FEATURES)[keyof typeof SUBSCRIPTION_FEATURES];

/**
 * Return type for the useSubscriptionPlanFeatures hook
 */
export interface UseSubscriptionPlanFeaturesReturn {
  subscriptionPlanFeatures: SubscriptionPlanFeatures | undefined;
  hasFeatureAccess: (feature: SubscriptionFeature) => boolean;
  loading: boolean;
  error: boolean;
}

/**
 * GraphQL query to fetch subscription plan features for a business unit
 */
export const subscriptionFeaturesQuery = graphql(`
  query GetSubscriptionFeatures($businessUnit: Int!) {
    businessUnit(businessUnit: $businessUnit) {
      id
      subscriptionPlanFeatures {
        slug
        isActive
      }
    }
  }
`);

/**
 * Type definition for subscription plan features data
 */
export type SubscriptionPlanFeatures = NonNullable<
  ResultOf<typeof subscriptionFeaturesQuery>["businessUnit"]
>["subscriptionPlanFeatures"];

/**
 * Custom hook to check subscription plan features for the current business unit
 *
 * @returns Object containing subscription features data and utility functions
 */
export const useSubscriptionPlanFeatures = (): UseSubscriptionPlanFeaturesReturn => {
  const { activeBusinessUnitIdInt, loading: businessUnitLoading } = useActiveBusinessUnit();

  const { data, loading, error } = useQuery(subscriptionFeaturesQuery, {
    variables: {
      businessUnit: activeBusinessUnitIdInt!,
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    skip: !activeBusinessUnitIdInt,
  });

  const subscriptionPlanFeatures = data?.businessUnit?.subscriptionPlanFeatures ?? undefined;

  /**
   * Check if a specific feature is enabled for the current business unit
   * Implements fail-open strategy: defaults to true during loading or errors
   */
  const hasFeatureAccess = (feature: SubscriptionFeature): boolean => {
    // Fail-open strategy: allow access during loading, business unit loading, or network errors
    if (loading || businessUnitLoading || error) {
      return true;
    }

    // If no subscription data is available, deny access
    if (!subscriptionPlanFeatures) {
      return false;
    }

    // If subscription features array is empty, deny access
    if (subscriptionPlanFeatures.length === 0) {
      return false;
    }

    // Check if the specific feature is active
    return subscriptionPlanFeatures.some((f) => f.isActive && f.slug === feature);
  };

  return {
    subscriptionPlanFeatures,
    hasFeatureAccess,
    loading: businessUnitLoading || loading,
    error: Boolean(error),
  };
};
